import { KeyboardEvent } from 'react'
import { Editor, Node, Path, Range, Element as SlateElement, Transforms } from 'slate'

import {
  isEventWithHotKey,
  Modifiers,
  withHotKey,
  withModKey,
} from 'core-components/helpers/eventKeys'

import { BLOCK_FORMATS, INLINE_FORMATS, TCustomElement } from '../interface/custom-types'

import { enterBehavior } from './enterBehaviorHelper'
import { decreaseListLevel } from './listHelpers/decreaseListLevel'
import { increaseListLevel } from './listHelpers/increaseListLevel'
import { listsNodeHelper } from './listsHelper'
import { quitFromLink } from './quitNodeHelper'
import { toggleMark } from './slateHelper'

export const hotKeyHandler = (editor: Editor, event: KeyboardEvent) => {
  // Default left/right behavior is unit:'character'.
  // This fails to distinguish between two cursor positions, such as
  // <inline>foo<cursor/></inline> vs <inline>foo</inline><cursor/>.
  // Here we modify the behavior to unit:'offset'.
  // This lets the user step into and out of the inline without stepping over characters.
  // You may wish to customize this further to only use unit:'offset' in specific cases.
  const { selection } = editor
  const isEnter = event.key === 'Enter'
  const isHotKeyPressed = isEventWithHotKey(event.nativeEvent)
  const isModifiersKeyPressed = withModKey(event.nativeEvent)
  const isModifiersAndShiftKeysPressed =
    withModKey(event.nativeEvent) && withHotKey(event.nativeEvent, Modifiers.shiftKey)

  // soft line break
  if (isEnter && event.shiftKey) {
    event.preventDefault()
    Editor.insertSoftBreak(editor)
    return
  }

  if (selection && Range.isCollapsed(selection)) {
    if (event.key === 'ArrowLeft' && !isHotKeyPressed) {
      event.preventDefault()
      Transforms.move(editor, { unit: 'offset', reverse: true })
      return
    }
    if (event.key === 'ArrowRight' && !isHotKeyPressed) {
      event.preventDefault()
      Transforms.move(editor, { unit: 'offset' })
      return
    }

    // base enter
    if (isEnter && !isHotKeyPressed) {
      const expandNode = Editor.above(editor, {
        match: (node) =>
          SlateElement.isElement(node) && node.type === BLOCK_FORMATS.expandBlockTitle,
      })
      if (expandNode) {
        event.preventDefault()
        return
      }

      event.preventDefault()

      const opsBefore = editor.operations.length
      enterBehavior(editor, selection, event)
      const didChange = editor.operations.length > opsBefore

      if (!didChange) {
        Editor.insertBreak(editor)
      }
      return
    }

    if (event.key === ' ') {
      // when we copy and paste links, the selection remains inside the link node,
      // to exit this node when pressing space, we will use the function below
      quitFromLink(editor, selection, event)
    }
  }

  if (event.key === 'Tab') {
    if (selection) {
      const { isList, listFormat } = listsNodeHelper(editor, selection)
      // When pressing tab inside a list we need to increase the nesting of the list,
      // listFormat is needed to determine what kind of list we need to wrap
      if (isList && listFormat) {
        const isOneSelectedListItem = Path.equals(selection.anchor.path, selection.focus.path)
        const currentNodeString = Node.string(Node.get(editor, selection.anchor.path))
        const isStartOfListItem = currentNodeString.length && selection.anchor.offset === 0

        event.preventDefault()
        if (event.shiftKey) {
          decreaseListLevel(editor)
        } else if (isStartOfListItem || currentNodeString.length === 0 || !isOneSelectedListItem) {
          increaseListLevel(editor, listFormat)
        } else {
          Editor.insertText(editor, '\u2003') // tab
        }
        return
      }
    }

    event.preventDefault()
    Editor.insertText(editor, '\u2003')
    return
  }

  if (event.key.toLowerCase() === 'i' && isModifiersKeyPressed) {
    event.preventDefault()
    toggleMark(editor, INLINE_FORMATS.italic)
    return
  }
  if (event.key.toLowerCase() === 'b' && isModifiersKeyPressed) {
    event.preventDefault()
    toggleMark(editor, INLINE_FORMATS.bold)
    return
  }
  if (event.key.toLowerCase() === 'u' && isModifiersKeyPressed) {
    event.preventDefault()
    toggleMark(editor, INLINE_FORMATS.underline)
    return
  }
  if (event.key.toLowerCase() === 'x' && isModifiersAndShiftKeysPressed) {
    event.preventDefault()
    toggleMark(editor, INLINE_FORMATS.strikethrough)
    return
  }

  if (isEnter) {
    // this helps us exit the node when we press enter while inside the button
    if (selection && Node.has(editor, Path.parent(selection.anchor.path))) {
      const currentNode = Node.get(editor, Path.parent(selection.anchor.path))
      const isLinkButtonNode =
        currentNode &&
        (currentNode as TCustomElement).type === BLOCK_FORMATS.link &&
        (currentNode as any)['view'] === 'button'
      if (isLinkButtonNode) {
        event.preventDefault()
        return
      }
    }
  }
}
